import React from 'react';

export interface DonutChartSegment {
  label: string;
  value: number;
  color: string;
  percentage: number;
}

export interface DonutChartProps {
  segments: DonutChartSegment[];
  total: number;
  size?: number;
  strokeWidth?: number;
  showCenterValue?: boolean;
  showPercentages?: boolean;
  showLegend?: boolean;
  className?: string;
}

const DonutChart: React.FC<DonutChartProps> = ({
  segments,
  total,
  size = 64,
  strokeWidth = 8,
  showCenterValue = true,
  showPercentages = true,
  showLegend = true,
  className = ""
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  let currentOffset = 0;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* Donut Chart */}
      <div className="relative" style={{ width: size, height: size }}>
        <svg 
          width={size} 
          height={size} 
          className="transform -rotate-90"
          viewBox={`0 0 ${size} ${size}`}
        >
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#524545"
            strokeWidth={strokeWidth}
          />
          
          {/* Segments */}
          {segments.map((segment, index) => {
            const segmentLength = (segment.percentage / 100) * circumference;
            const segmentOffset = currentOffset;
            
            currentOffset += segmentLength;
            
            return (
              <circle
                key={index}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${segmentLength} ${circumference}`}
                strokeDashoffset={-segmentOffset}
                strokeLinecap="round"
              />
            );
          })}
        </svg>
        
        {/* Center value */}
        {showCenterValue && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white text-[8px] font-bold">
              {total > 0 ? `${Math.round(Math.max(...segments.map(s => s.percentage)))}%` : '0%'}
            </span>
          </div>
        )}
      </div>
      
      {/* Legend */}
      {showLegend && (
        <div className="flex justify-center space-x-2 mt-3">
          {segments.map((segment, index) => (
            <div key={index} className="flex items-center space-x-1">
              <div 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: segment.color }}
              />
              <span className="text-[8px] text-white">{segment.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DonutChart;
